import { Stack, TextInput, Textarea, Switch, Button, Group } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { templateFormSchema, type TemplateFormData, defaultTemplateFormValues } from "../../forms/template-schema";

interface TemplateFormProperties {
  /**
   * Initial values for edit mode
   * If not provided, uses default values for create mode
   */
  initialValues?: Partial<TemplateFormData>;

  /**
   * Callback when form is submitted with valid data
   */
  onSubmit: (values: TemplateFormData) => void | Promise<void>;

  /**
   * Callback when form is cancelled
   */
  onCancel?: () => void;

  /**
   * Whether form is currently submitting (loading state)
   */
  isSubmitting?: boolean;

  /**
   * Submit button label
   * @default "Salvar"
   */
  submitLabel?: string;

  /**
   * Cancel button label
   * @default "Cancelar"
   */
  cancelLabel?: string;
}

/**
 * Form component for creating and editing report templates
 *
 * Features:
 * - Zod validation with real-time feedback
 * - Support for create and edit modes
 * - Loading states during submission
 * - Configurable labels and callbacks
 * @param root0
 * @param root0.initialValues
 * @param root0.onSubmit
 * @param root0.onCancel
 * @param root0.isSubmitting
 * @param root0.submitLabel
 * @param root0.cancelLabel
 * @example
 * ```tsx
 * // Create mode
 * <TemplateForm
 *   onSubmit={async (data) => {
 *     await createTemplate(data);
 *   }}
 *   onCancel={() => closeModal()}
 * />
 *
 * // Edit mode
 * <TemplateForm
 *   initialValues={existingTemplate}
 *   onSubmit={async (data) => {
 *     await updateTemplate(templateId, data);
 *   }}
 *   submitLabel="Atualizar"
 * />
 * ```
 */
export function TemplateForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitLabel = "Salvar",
  cancelLabel = "Cancelar"
}: TemplateFormProperties) {
  // Merge initial values with defaults to ensure all required fields are present
  const formInitialValues = Object.assign({}, defaultTemplateFormValues, initialValues) as TemplateFormData;

  const form = useForm<TemplateFormData>({
    validate: zodResolver(templateFormSchema),
    initialValues: formInitialValues
  });

  const handleSubmit = form.onSubmit(async (values) => {
    await onSubmit(values);
  });

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {/* Nome */}
        <TextInput
          label="Nome do Template"
          description="Nome exibido ao selecionar o template"
          placeholder="Ex: Relatório Padrão"
          required
          withAsterisk
          {...form.getInputProps("nome")}
          disabled={isSubmitting}
        />

        {/* Descrição */}
        <Textarea
          label="Descrição"
          description="Descreva o propósito e características deste template"
          placeholder="Ex: Template completo com gráficos, tabelas e análise detalhada"
          required
          withAsterisk
          minRows={3}
          maxRows={6}
          autosize
          {...form.getInputProps("descricao")}
          disabled={isSubmitting}
        />

        {/* Ativo */}
        <Switch
          label="Template Ativo"
          description="Templates inativos não aparecem na seleção"
          {...form.getInputProps("ativo", { type: "checkbox" })}
          disabled={isSubmitting}
        />
        <Switch
          label="Acessível para Convidada"
          description="Permite que usuários com perfil 'convidada' acessem este template"
          {...form.getInputProps("acessivelConvidada", { type: "checkbox" })}
          disabled={isSubmitting}
        />
        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          {onCancel && (
            <Button variant="subtle" onClick={onCancel} disabled={isSubmitting}>
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" loading={isSubmitting}>
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
